import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2, Save, Plug, Copy, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { adminGetPaymentConfig, adminUpdatePaymentConfig, adminTestPaymentConnection, PaymentConfig, TestResult } from '../../features/payments/api/paymentConfig';
import { PAYMENTS } from '../../constants/testIds';

const inputClass = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

export default function AdminPaymentGatewayPage() {
  const [cfg, setCfg] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [apiKey, setApiKey] = useState('');

  const load = () => {
    setLoading(true);
    adminGetPaymentConfig()
      .then((c) => {
        if (!c.public_base_url) {
          c = { ...c, public_base_url: process.env.REACT_APP_BACKEND_URL || window.location.origin };
        }
        setCfg(c);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k: keyof PaymentConfig, v: any) => setCfg((c) => (c ? { ...c, [k]: v } : c));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload: any = {
        mode: cfg.mode, env: cfg.env, merchant_code: cfg.merchant_code,
        signature_mode: cfg.signature_mode, public_base_url: cfg.public_base_url,
        default_payment_method: cfg.default_payment_method, expiry_period: Number(cfg.expiry_period) || 60,
      };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const updated = await adminUpdatePaymentConfig(payload);
      setCfg(updated); setApiKey('');
      toast.success('Konfigurasi pembayaran tersimpan');
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const res = await adminTestPaymentConnection();
      setResult(res);
      if (res.ok) toast.success('Koneksi Duitku berhasil'); else toast.error('Koneksi gagal', { description: res.message });
    } catch (e: any) { toast.error('Gagal', { description: e.message }); }
    finally { setTesting(false); }
  };

  const copy = (text: string) => { navigator.clipboard.writeText(text); toast.success('Disalin'); };

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Payment Gateway">
      <div className="max-w-2xl" data-testid={PAYMENTS.adminPage}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><CreditCard className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
          <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Integrasi</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Payment Gateway (Duitku)</h2></div>
        </div>

        {loading || !cfg ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div> : (
          <div className="space-y-5">
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Mode</label>
                  <select data-testid={PAYMENTS.mode} className={inputClass} value={cfg.mode} onChange={(e) => set('mode', e.target.value)}>
                    <option value="mock">Mock (Dev/Testing)</option>
                    <option value="duitku">Duitku (Live API)</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Environment</label>
                  <select data-testid={PAYMENTS.env} className={inputClass} value={cfg.env} onChange={(e) => set('env', e.target.value)}>
                    <option value="sandbox">Sandbox</option>
                    <option value="production">Production</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Merchant Code</label>
                <input data-testid={PAYMENTS.merchantCode} className={inputClass} value={cfg.merchant_code} onChange={(e) => set('merchant_code', e.target.value)} placeholder="DXXXX" />
              </div>
              <div>
                <label className={labelClass}>API Key {cfg.api_key_set && <span className="text-emerald-400 normal-case tracking-normal">· tersimpan ({cfg.api_key_preview})</span>}</label>
                <input data-testid={PAYMENTS.apiKey} type="password" className={inputClass} value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={cfg.api_key_set ? 'Biarkan kosong untuk mempertahankan key' : 'Masukkan API Key Duitku'} />
                <p className="text-[11px] text-white/30 mt-1">API Key disimpan aman di server dan tidak pernah ditampilkan kembali.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Signature</label>
                  <select className={inputClass} value={cfg.signature_mode} onChange={(e) => set('signature_mode', e.target.value)}>
                    <option value="hmac_sha256">HMAC-SHA256 (disarankan)</option>
                    <option value="md5">MD5 (legacy)</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Expiry (menit)</label>
                  <input data-testid={PAYMENTS.expiry} type="number" className={inputClass} value={cfg.expiry_period} onChange={(e) => set('expiry_period', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Metode Pembayaran Default</label>
                <input data-testid={PAYMENTS.defaultMethod} className={inputClass} value={cfg.default_payment_method} onChange={(e) => set('default_payment_method', e.target.value)} placeholder="Kosongkan agar pelanggan memilih di halaman Duitku" />
              </div>
              <div>
                <label className={labelClass}>Public Base URL</label>
                <input data-testid={PAYMENTS.publicUrl} className={inputClass} value={cfg.public_base_url} onChange={(e) => set('public_base_url', e.target.value)} placeholder="https://app-anda.com" />
              </div>
            </div>

            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-3">
              <p className="text-sm uppercase tracking-wider text-white/70">Callback URL</p>
              <p className="text-[11px] text-white/40">Daftarkan URL ini di dashboard Duitku (project → callback).</p>
              <div className="flex items-center gap-2">
                <input data-testid={PAYMENTS.callbackUrl} readOnly value={cfg.callback_url || '(isi Public Base URL lalu simpan)'} className={`${inputClass} font-mono text-xs`} />
                <button data-testid={PAYMENTS.copyCallback} onClick={() => copy(cfg.callback_url)} disabled={!cfg.callback_url} className="p-2.5 rounded-lg border border-white/10 text-white/60 hover:text-cyan-400 hover:bg-white/5 disabled:opacity-40"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
              <div className="flex items-center gap-2">
                <input readOnly value={cfg.return_url || '(isi Public Base URL lalu simpan)'} className={`${inputClass} font-mono text-xs`} />
                <button onClick={() => copy(cfg.return_url)} disabled={!cfg.return_url} className="p-2.5 rounded-lg border border-white/10 text-white/60 hover:text-cyan-400 hover:bg-white/5 disabled:opacity-40"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button data-testid={PAYMENTS.saveBtn} onClick={save} disabled={saving} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Save className="w-4 h-4" strokeWidth={2} />} Simpan
              </button>
              <button data-testid={PAYMENTS.testBtn} onClick={test} disabled={testing} className="border border-white/15 text-white px-6 py-2.5 rounded-lg transition-colors hover:bg-white/5 disabled:opacity-50 flex items-center gap-2">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Plug className="w-4 h-4" strokeWidth={2} />} Test Koneksi
              </button>
            </div>

            {result && (
              <div data-testid={PAYMENTS.testResult} className={`rounded-2xl p-5 border ${result.ok ? 'border-emerald-400/30 bg-emerald-400/[0.06]' : 'border-red-400/30 bg-red-400/[0.06]'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {result.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={1.5} /> : <XCircle className="w-5 h-5 text-red-400" strokeWidth={1.5} />}
                  <p className={`text-sm font-medium ${result.ok ? 'text-emerald-300' : 'text-red-300'}`}>{result.ok ? 'Koneksi berhasil' : 'Koneksi gagal'}</p>
                </div>
                {result.message && <p className="text-xs text-white/50 mb-3">{result.message}</p>}
                {result.methods.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {result.methods.map((m) => (
                      <span key={m.paymentMethod} className="text-[11px] text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 px-2.5 py-1 rounded-full">{m.paymentName} ({m.paymentMethod})</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
